import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { addMonths, format, isSameDay, parseISO, startOfDay } from "date-fns";
import { max, minBy } from "lodash-es";
import { usePostHog } from "posthog-js/react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router";

import { AskNobiButton } from "@/components/AskNobi/AskNobiButton";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { ConfirmModal } from "@/components/ConfirmModal/ConfirmModal";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { formatDate } from "@/components/FormattedDate/FormattedDate";
import { Download01Icon } from "@/components/Icons/Download01Icon";
import { FilterFunnel01Icon } from "@/components/Icons/FilterFunnel01Icon";
import { Grid01Icon } from "@/components/Icons/Grid01Icon";
import { ListIcon } from "@/components/Icons/ListIcon";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { PeriodPicker } from "@/components/PeriodPicker/PeriodPicker";
import { filterProjectsByFilters } from "@/components/ProjectFilter/filterProjectsByFilters";
import { hasFilterableProjects } from "@/components/ProjectFilter/hasFilterableProjects";
import type { PortfolioProjectFilterValues } from "@/components/ProjectFilter/PortfolioProjectFilterFields";
import { RadioCard } from "@/components/Radio/RadioCard";
import { Redirect } from "@/components/Routing/Redirect";
import { SearchInput } from "@/components/SearchInput/SearchInput";
import { Box } from "@/design-system/Box";
import { Button } from "@/design-system/Button";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { Inline } from "@/design-system/Inline";
import { NumericBadge } from "@/design-system/NumericBadge";
import { Select } from "@/design-system/Select";
import { Stack } from "@/design-system/Stack";
import { MAX_ANALYTICS_DATE, MIN_ANALYTICS_DATE } from "@/helpers/analytics";
import { hasAdminProject } from "@/helpers/portfolio-access";
import { parseQueryParamToList } from "@/helpers/query-params";
import { LOCAL_STORAGE_KEYS } from "@/helpers/storage";
import { useLinkedProjects } from "@/hooks/Network/useLinkedProjects";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useBool } from "@/hooks/useBool";
import { useDebounceValue } from "@/hooks/useDebounce";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { getLocalStorageValue, useUpdateLocalStorage } from "@/hooks/useLocalStorage";
import { useQueryParams } from "@/hooks/useQueryParam";
import { useShouldSubmitOnEnter } from "@/hooks/useShouldSubmitOnEnter";
import { portfolioMutations } from "@/queries/portfolio/mutations";
import { usePortfolioQueries } from "@/queries/portfolio/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import { PortfolioProjectFilterDrawer } from "../../components/PortfolioProjectFilterDrawer";
import type { PortfolioRouteParams } from "../../routes";
import { AddProjectModal } from "./components/AddProjectModal";
import { GraphView } from "./components/GraphView";
import { ListView } from "./components/ListView";
import { PortfolioOverviewEmptyState } from "./components/PortfolioOverviewEmptyState";
import { TilesView } from "./components/TilesView";

const TODAY_DATE = startOfDay(new Date());

export function PortfolioOverviewLegacyPage(): React.ReactNode {
  const [isFilterOpened, filtersOpenHandlers] = useBool();
  const [addProjectModalOpened, addProjectModalOpenedHandlers] = useBool();
  const [isConfirmExportModalOpened, confirmExportModalHandlers] = useBool();
  const [selectedView, setSelectedView] = useState(
    getLocalStorageValue<"tiles" | "list">(LOCAL_STORAGE_KEYS.PORTFOLIO_VIEW_MODE, "tiles"),
  );
  const [selectedDate, setSelectedDate] = useState(TODAY_DATE);
  const [search, setSearch] = useState("");
  const shouldSubmitOnEnter = useShouldSubmitOnEnter();

  const [queryParams, setQueryParams] = useQueryParams<PortfolioRouteParams["overview_LEGACY"]>();

  const { t, i18n } = useTranslation();
  const sessionUser = useSessionUser();
  const postHog = usePostHog();
  const navigate = useNavigate();
  const shouldShowLiveStats = isSameDay(selectedDate, TODAY_DATE);

  const portfolioQueries = usePortfolioQueries();
  const {
    data: portfolio,
    error: portfolioError,
    isRefetching: isRefetchingPortfolio,
    isPending: isPendingPortfolio,
  } = useQuery({
    ...portfolioQueries.list({
      date: format(shouldShowLiveStats ? MAX_ANALYTICS_DATE : selectedDate, "yyyy-MM-dd"),
    }),
    placeholderData: keepPreviousData,
  });

  const { data: linkedProjects, error: linkedProjectsError, isPending: isPendingLinkedProjects } = useLinkedProjects();
  const exportProjectAnalyticsMutation = portfolioMutations.useExportProjectAnalytics();

  useUpdateLocalStorage(LOCAL_STORAGE_KEYS.PORTFOLIO_VIEW_MODE, selectedView);
  useDocumentTitle(t("page.portfolio.title"));

  const handleAddNewProject = () => {
    postHog.capture("clicked_portfolio_overview_upsell_cta");

    addProjectModalOpenedHandlers.setTrue();
  };

  const debouncedQuery = useDebounceValue(search.toLowerCase().trim());
  const currFilter: PortfolioProjectFilterValues = useMemo(() => {
    return {
      city: parseQueryParamToList(queryParams.city),
      owner: parseQueryParamToList(queryParams.owner),
      maintenance: parseQueryParamToList(queryParams.maintenance),
    };
  }, [queryParams.city, queryParams.owner, queryParams.maintenance]);

  const filteredProjects = useMemo(
    () =>
      filterProjectsByFilters(portfolio?.items || [], {
        city: currFilter.city,
        owner: currFilter.owner,
        maintenance: currFilter.maintenance,
        search: debouncedQuery,
      }),
    [portfolio?.items, currFilter, debouncedQuery],
  );

  // oxlint-disable-next-line react-hooks/preserve-manual-memoization
  const canFilter = useMemo(() => portfolio?.items && hasFilterableProjects(portfolio.items), [portfolio?.items]);

  if (portfolioError || linkedProjectsError) {
    return <ErrorPage error={portfolioError ?? linkedProjectsError} />;
  }

  if (isPendingPortfolio || isPendingLinkedProjects) {
    return <FullSizeLoader withPadding />;
  }

  // Redirect non-admin users to their (fallback) project
  if (!hasAdminProject(linkedProjects)) {
    return <Redirect to={() => routes.home.home({ slug: sessionUser.project.slug })} />;
  }

  const handleUpdateProjectFilter = (newFilter: PortfolioProjectFilterValues) => {
    setQueryParams({
      ...queryParams,
      city: newFilter.city?.join(",") || undefined,
      owner: newFilter.owner?.join(",") || undefined,
      maintenance: newFilter.maintenance?.join(",") || undefined,
    });
  };

  const handleClickExportAnalytics = () => {
    postHog.capture("clicked_export_portfolio_analytics", {
      projectIds: filteredProjects.map((project) => project.id),
    });

    confirmExportModalHandlers.setTrue();
  };

  const exportAnalytics = () => {
    exportProjectAnalyticsMutation.mutate(
      {
        data: {
          projectIds: filteredProjects.map((project) => project.id),
        },
        fileName: `portfolio_analytics_${format(new Date(), "yyyy-MM-dd")}_last_90_days..xlsx`,
      },
      {
        onSettled: () => {
          confirmExportModalHandlers.setFalse();
        },
      },
    );
  };

  const minProjectCreationDate = minBy(
    portfolio.items.map((x) => parseISO(x.createdAt)),
    (x) => x.valueOf(),
  );
  const amountActiveFilters = Object.values(currFilter).filter(
    (value) => Array.isArray(value) && value.length > 0,
  ).length;
  // The metadata we need on live data can be assumed to be the same across all projects (e.g. `from` date)
  const anyProjectWithLiveData = portfolio.items.find((project) => Boolean(project.liveStats));

  return (
    <>
      <DocumentPage
        containerBoundary="inherit"
        tabTitle={t("page.portfolio.title")}
        title={t("page.portfolio.header.title", {
          name: sessionUser.fullName,
        })}
        breadcrumbs={
          <Breadcrumbs
            pages={[
              {
                name: t("page.portfolio.title"),
              },
              {
                name: t("page.portfolio.overview.title"),
              },
            ]}
          />
        }
        actions={
          <AskNobiButton
            defaultFilters={{}}
            context={{
              scope: "portfolio",
              entity: "portfolio",
              page: "detail",
              filters: {
                city: currFilter.city,
                owner: currFilter.owner,
                maintenance: currFilter.maintenance,
              },
            }}
            suggestions={[t("component.ask-nobi-chat-drawer.portfolio.suggestion.1")]}
          />
        }
        header={
          <section className="flex w-full flex-col items-start justify-between gap-space-md @md:flex-row @md:flex-wrap @md:items-center">
            <section className="flex flex-col items-start gap-space-sm @sm:flex-row @sm:items-center">
              {portfolio.items.length > 8 && (
                <SearchInput
                  width="standard"
                  placeholder={t("component.project-switcher.search.placeholder")}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={async (e) => {
                    if (e.key === "Enter" && filteredProjects.length === 1) {
                      await navigate(routes.home.home({ slug: filteredProjects[0].slug }));
                    }
                  }}
                  autoFocus={shouldSubmitOnEnter}
                />
              )}
              {canFilter && (
                <Button
                  data-testid="portfolio-filter-btn"
                  icon={<FilterFunnel01Icon />}
                  variant="soft"
                  onClick={filtersOpenHandlers.toggle}
                  aria-pressed={isFilterOpened}
                >
                  <Inline gap="sm" align="center" wrap={false}>
                    {t("common.action.filter")}
                    {amountActiveFilters > 0 && <NumericBadge count={amountActiveFilters} variant="filled" />}
                  </Inline>
                </Button>
              )}
              <PeriodPicker
                type="month"
                value={selectedDate}
                onChange={setSelectedDate}
                min={max([MIN_ANALYTICS_DATE, minProjectCreationDate])}
                max={MAX_ANALYTICS_DATE}
                renderSelected={(date) => {
                  if (date.getTime() === TODAY_DATE.getTime()) {
                    return t("page.portfolio.overview.filters.month.latest");
                  }

                  return formatDate(i18n, "monthYear", date);
                }}
                renderFooterComponent={
                  anyProjectWithLiveData?.liveStats
                    ? () => (
                        <Box padding={{ y: "sm" }}>
                          <RadioCard
                            name="last30Days"
                            onChange={() => setSelectedDate(TODAY_DATE)}
                            value={TODAY_DATE.toDateString()}
                            checked={isSameDay(selectedDate, TODAY_DATE)}
                            label={t("page.portfolio.overview.filters.month.latest")}
                            description={`${formatDate(i18n, "dateMonthYearShort", anyProjectWithLiveData.liveStats!.from)} - ${formatDate(i18n, "dateMonthYearShort", addMonths(anyProjectWithLiveData.liveStats!.from, 1))}`}
                          />
                        </Box>
                      )
                    : undefined
                }
              />
            </section>
            <Inline gap="sm" align="center">
              <div className="w-control-narrow">
                <Select
                  id="view"
                  items={["tiles", "list"] as const}
                  keySelector={(option) => option}
                  renderOption={(option) => (
                    <Inline gap="sm" align="center" wrap={false} as="span">
                      {option === "tiles" ? <Grid01Icon /> : <ListIcon />}
                      <span>
                        {option === "tiles"
                          ? t("page.portfolio.overview.filters.view.tiles")
                          : t("page.portfolio.overview.filters.view.list")}
                      </span>
                    </Inline>
                  )}
                  selected={selectedView}
                  onChange={setSelectedView}
                />
              </div>
              {sessionUser.isSuperAdmin && (
                <Button
                  data-testid="export-analytics-btn"
                  title={t("page.portfolio.overview.export.tooltip")}
                  variant="soft"
                  icon={<Download01Icon />}
                  onClick={handleClickExportAnalytics}
                >
                  {t("common.action.export")}
                </Button>
              )}
            </Inline>
          </section>
        }
      >
        <Stack gap="md">
          <Stack gap="sm">
            <span className="text-caption">
              {t("page.portfolio.overview.label.count", {
                count: filteredProjects.length,
              })}
            </span>
            {filteredProjects.length === 0 ? (
              <PortfolioOverviewEmptyState onAddProject={handleAddNewProject} />
            ) : (
              <>
                {selectedView === "list" && (
                  <ListView
                    items={filteredProjects}
                    showLiveStats={shouldShowLiveStats}
                    selectedDate={format(shouldShowLiveStats ? MAX_ANALYTICS_DATE : selectedDate, "yyyy-MM-dd")}
                    isLoading={isRefetchingPortfolio}
                  />
                )}
                {selectedView === "tiles" && (
                  <TilesView
                    items={filteredProjects}
                    showLiveStats={shouldShowLiveStats}
                    onShowAddProject={handleAddNewProject}
                  />
                )}
              </>
            )}
          </Stack>

          {filteredProjects.length > 0 && (
            <GraphView date={shouldShowLiveStats ? MAX_ANALYTICS_DATE : selectedDate} items={filteredProjects} />
          )}
        </Stack>
      </DocumentPage>
      <PortfolioProjectFilterDrawer
        isOpened={isFilterOpened}
        onOpenChange={filtersOpenHandlers.set}
        onUpdate={handleUpdateProjectFilter}
        projects={linkedProjects}
        selectedFilter={{
          city: currFilter.city || [],
          maintenance: currFilter.maintenance || [],
          owner: currFilter.owner || [],
        }}
      />
      <ConfirmModal
        tone="primary"
        isOpened={isConfirmExportModalOpened}
        onOpenChange={confirmExportModalHandlers.set}
        isLoading={exportProjectAnalyticsMutation.isPending}
        title={t("page.portfolio.overview.export-analytics-modal.title")}
        description={t("page.portfolio.overview.export-analytics-modal.description", {
          projectCount: filteredProjects.length,
        })}
        data-testid="portfolio-confirm-export-modal"
        onReject={() => confirmExportModalHandlers.setFalse()}
        onResolve={exportAnalytics}
        resolveBtnProps={{
          text: t("common.action.export"),
          "data-testid": "confirm-export-btn",
        }}
      />
      <AddProjectModal isOpened={addProjectModalOpened} onOpenChange={addProjectModalOpenedHandlers.set} />
    </>
  );
}
